import { collection, addDoc, getDocs, query, where, getDoc, doc, updateDoc, serverTimestamp, orderBy } from "firebase/firestore";

import { getAuthUserId } from "./AuthService";
import { firestore } from "./FirebaseService";
import { EmailTemplateIds } from "../utils/MailUtils";

const mailCollection = collection(firestore, "mail");
const emailTemplatesCollection = collection(firestore, "emailTemplates");

/**
 * Delivery status for display from SendGrid webhook shape only:
 * `delivery.event.status`, or string `delivery.event`. Does not use `delivery.state`
 * (Trigger Email extension lifecycle: PENDING/SUCCESS/ERROR — not an event type).
 */
export const getMailDeliveryStatus = (delivery) => {
  if (!delivery || typeof delivery !== "object") return null;
  const event = delivery.event;
  if (event != null && typeof event === "object" && event.status != null && event.status !== "") {
    return event.status;
  }
  if (typeof event === "string" && event.length > 0) {
    return event;
  }
  return null;
};

export const getMailDeliveryReason = (delivery) => {
  if (!delivery || typeof delivery !== "object") return null;
  const event = delivery.event;
  if (event && typeof event === "object") {
    return event.reason ?? null;
  }
  return null;
};

/**
 * Fetches all mail documents sent to the given recipient email.
 * Returns an array of { sentAt, templateId, category } sorted by sentAt descending.
 * sentAt comes from delivery.startTime if present (Firebase SendGrid extension).
 */
export const getSentMail = async (recipientEmail) => {
  if (!recipientEmail) return [];
  const q = query(
    mailCollection,
    where("to", "array-contains", recipientEmail)
  );
  const snapshot = await getDocs(q);
  const items = snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    const delivery = data.delivery || {};
    const startTime = delivery.startTime;
    const sentAt = startTime?.toDate ? startTime.toDate() : null;
    const templateId = data.sendGrid?.templateId ?? null;
    const category = Array.isArray(data.categories) && data.categories.length > 0 ? data.categories[0] : null;
    const sentBy = data.from ?? data.message?.from ?? null;
    const status = getMailDeliveryStatus(delivery);
    const reason = getMailDeliveryReason(delivery);
    const resolutionStatus = delivery?.resolution?.status ?? null;
    const resolutionResolvedBy = delivery?.resolution?.resolvedBy ?? null;
    const resolutionUpdatedAt = delivery?.resolution?.updatedAt ?? null;
    const triggeredBy = data.triggeredBy ?? null;
    return {
      id: docSnap.id,
      sentAt,
      templateId,
      category,
      sentBy,
      triggeredBy,
      status,
      reason,
      resolutionStatus,
      resolutionResolvedBy,
      resolutionUpdatedAt
    };
  });
  items.sort((a, b) => {
    const tA = a.sentAt ? a.sentAt.getTime() : 0;
    const tB = b.sentAt ? b.sentAt.getTime() : 0;
    return tB - tA;
  });
  return items;
};

export const getMailById = async (mailId) => {
  if (!mailId) return null;
  const docSnap = await getDoc(doc(firestore, "mail", mailId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
};

export const getEmailTemplateById = async (templateId) => {
  if (!templateId) return null;
  const docSnap = await getDoc(doc(firestore, "emailTemplates", templateId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
};

export const markMailManuallyResolved = async (mailId, resolvedBy) => {
  if (!mailId) return;
  const mailDocRef = doc(firestore, "mail", mailId);
  await updateDoc(mailDocRef, {
    "delivery.resolution": {
      status: "manually_resolved",
      updatedAt: serverTimestamp(),
      ...(resolvedBy ? { resolvedBy } : {})
    }
  });
};

export const sendTemplateEmail = async (to, bcc, templateKey, dynamicTemplateData = {}) => {
  try {
    const triggeredBy = getAuthUserId();
    const mailData = {
      to: [to],
      bcc,
      ...(triggeredBy ? { triggeredBy } : {}),
      sendGrid: {
        templateId: EmailTemplateIds[templateKey],
        dynamicTemplateData: {...dynamicTemplateData}
      },
      categories: [templateKey]
    };

    const docRef = await addDoc(mailCollection, mailData);
    console.log(`Template email sent with ID ${docRef.id} to ${to} and bcc ${bcc} with data`, dynamicTemplateData);
  }
  catch (error) {
    console.error("Error sending template email:", error);
    throw new Error(`No se ha podido enviar el correo, vuelve a intentarlo. ${error.message}`);
  }
};

export const sendEmail = async (to, bcc, subject, text, html, type) => {
  try {
    const triggeredBy = getAuthUserId();
    const mailData = {
      to: [to],
      bcc,
      ...(triggeredBy ? { triggeredBy } : {}),
      message: {
        subject,
        text,
        html
      },
      categories: [type]
    };

    const docRef = await addDoc(mailCollection, mailData);
    console.log(`Email sent with ID ${docRef.id} to ${to}`);
  }
  catch (error) {
    console.error("Error sending email:", error);
    throw new Error("Failed to send email. Please try again later.");
  }
};

/**
 * Fetches mail docs "needing attention" based on delivery event status.
 * Simple rule:
 * - delivery.event.status must exist
 * - status must not be "open" nor "delivered"
 * - show also manually resolved mails (so admin can see what happened)
 *
 * Sorted by `delivery.startTime` desc (most recent first).
 */
export const getPendingAttentionMails = async () => {
  const q = query(
    mailCollection,
    where("delivery.event.status", "not-in", ["open", "delivered", "processed"]),
    orderBy("delivery.startTime", "desc")
  );
  const snapshot = await getDocs(q);

  const items = snapshot.docs
    .map((docSnap) => {
      const data = docSnap.data();
      const delivery = data.delivery || {};
      const startTime = delivery.startTime;
      const sentAt = startTime?.toDate ? startTime.toDate() : null;

      const eventStatus = getMailDeliveryStatus(delivery);
      const eventReason = getMailDeliveryReason(delivery);
      // Safety guard in case some docs have non-standard event shapes.
      if (!eventStatus) return null;

      const category = Array.isArray(data.categories) && data.categories.length > 0 ? data.categories[0] : null;
      const patientEmail = Array.isArray(data.to) && data.to.length > 0 ? data.to[0] : null;
      const subject = data.message?.subject ?? null;
      const resolutionStatus = delivery?.resolution?.status ?? null;
      const resolutionResolvedBy = delivery?.resolution?.resolvedBy ?? null;
      const resolutionUpdatedAt = delivery?.resolution?.updatedAt ?? null;
      const triggeredBy = data.triggeredBy ?? null;

      return {
        id: docSnap.id,
        sentAt,
        category,
        subject,
        patientEmail,
        triggeredBy,
        status: eventStatus,
        reason: eventReason,
        resolutionStatus,
        resolutionResolvedBy,
        resolutionUpdatedAt
      };
    })
    .filter(Boolean);

  return items;
};